const KEY = "openclaw.control.settings.v1";

import {
  DEFAULT_CHAT_ASSIST_DISPLAY,
  migrateChatAssistDisplay,
  type ChatAssistDisplay,
} from "./chat/chat-assist-display.ts";
import { normalizeChatSendOptionsBySession } from "./chat/chat-send-options.ts";
import { type FleetComposerMode, isFleetComposerMode } from "./chat/fleet-composer-mode.ts";
import {
  canonicalizeSessionKeyForRehydrate,
  guardPersistedSessionKey,
  isPersistedFork,
} from "./fork-session-key.ts";
import type { ThemeMode, CustomThemeSettings } from "./theme.ts";
import { type FleetChatMainTabId } from "./views/fleet-chat-main-tabs.ts";
import {
  DEFAULT_FLEET_CHAT_PANELS_COLLAPSED,
  type FleetChatPanelsCollapsed,
} from "./views/fleet-collapsible-section.ts";
import {
  fleetMainTabToInspectorTab,
  migrateFleetWorkspaceLayouts,
  normalizeFleetWorkspaceDensity,
  normalizeFleetWorkspacePreset,
  type FleetInspectorTabId,
  type FleetWorkspaceLayoutState,
  type FleetWorkspacePreset,
} from "./views/fleet-workspace-presets.ts";

export type { ChatAssistDisplay, FleetComposerMode };

export const DEFAULT_FLEET_CHAT_MAIN_TAB: FleetChatMainTabId = "chat";

export type UiSettings = {
  gatewayUrl: string;
  token: string;
  sessionKey: string;
  lastActiveSessionKey: string;
  theme: ThemeMode;
  chatFocusMode: boolean;
  /** Centered transcript layout: hide avatars/footers, ~720px column. */
  chatTranscriptMode: boolean;
  /** Expand reasoning blocks by default (display only; not gateway reasoningLevel). */
  chatReasoningExpanded: boolean;
  /** Assistant / tool / subagent transcript visibility in chat. */
  chatAssistDisplay: ChatAssistDisplay;
  /** Open a Fleet Control tab when a subagent session is spawned. */
  chatAutoOpenSubagentTabs: boolean;
  /**
   * Auto-jump to the first failed/cancelled child session when a fan-out
   * settles with `needs_attention`. I5 invariant: operator stays on parent
   * unless they explicitly open the child. Defaults to `false` so the
   * parent chrome stays authoritative; operators opt in via Settings.
   */
  fleetAutoJumpToFailedChild: boolean;
  splitRatio: number; // Sidebar split ratio (0.4 to 0.7, default 0.6)
  navCollapsed: boolean; // Collapsible sidebar state
  navGroupsCollapsed: Record<string, boolean>; // Which nav groups are collapsed
  /** Open Fleet Control chat session tabs (session keys). */
  chatOpenTabs: string[];
  /** Session keys hidden from chat history (archived in UI only). */
  chatArchivedSessionKeys: string[];
  /**
   * Session keys the operator starred in the chat-history sidebar
   * (JAISIU-3328 / UX-R3). Persisted across reloads. Pinned to the top
   * of the sidebar by `partitionByFavorite` + the sidebar's Pinned
   * section. Empty array means no favorites — the legacy default.
   */
  favoriteChats: string[];
  /** Collapse Cursor-style chat history sidebar. */
  fleetChatHistoryCollapsed: boolean;
  /**
   * Inspector panel width index (JAISIU-3081 + JAISIU-3082).
   * 0 = compact, 1 = default, 2 = wide. The actual pixel widths are
   * derived in chat.ts via INSPECTOR_WIDTH_PRESETS. 0 used to mean
   * "use preset default"; values > 0 were raw pixel widths before
   * JAISIU-3082. Values >= 3 are clamped on load.
   */
  inspectorWidth: number;
  /** Hide multi-agent suggestion banner until next page load. */
  multiAgentHintDismissed: boolean;
  /** Collapsed Fleet Control chat sections (true = folded). */
  chatPanelsCollapsed: FleetChatPanelsCollapsed;
  /** Manual fleet map node positions (browser-local layout). */
  fleetGraphNodePositions: Record<string, { x: number; y: number }>;
  /** Active panel in Fleet Control main tab bar. */
  fleetChatMainTab: FleetChatMainTabId;
  /** Hide session key rows (1–3), active line, and open-tab strip for more chat space. */
  fleetSessionChromeCollapsed: boolean;
  /**
   * When true, session navigator sits in the Chat tab with a capped scroll area.
   * When false, classic layout: navigator above all main tabs (full height cap).
   */
  fleetSessionNavigatorScrollable: boolean;
  /** Hide target/model composer toolbar; keep message box only. */
  fleetChatComposerCollapsed: boolean;
  /** Collapse end-of-run file list above composer (header + actions stay visible). */
  fleetComposerFileReviewCollapsed: boolean;
  /** Fleet Control composer mode (Agent, Ask, Plan, Debug, Multitask). */
  chatComposerMode: FleetComposerMode;
  /** Per-session composer mode overrides. */
  chatComposerModeBySession?: Record<string, FleetComposerMode>;
  /** Logs tab scope follows Fleet session when true. */
  logsScopeLinked: boolean;
  /** Logs tab session path when unlinked (four segments). */
  logsScopePath: [string, string, string, string];
  /** Subsystem area filters enabled on Logs tab. */
  logsAreaFilters: string[];
  /** Include gateway/ws platform logs in scoped stream. */
  logsIncludePlatform: boolean;
  /** Tail full jaisiu JSONL file without session/area scoping. */
  logsViewAll: boolean;
  /** Sessions admin: list all rows from gateway. */
  sessionsViewAll: boolean;
  /** Sessions scope follows Fleet session when true. */
  sessionsScopeLinked: boolean;
  /** Sessions scope path when unlinked. */
  sessionsScopePath: [string, string, string, string];
  /** LLM Stats active sub-tab. */
  llmStatsTab: "connect" | "overview" | "analytics" | "fleet" | "sessions" | "trace" | "local";
  /** LLM Stats: include all sessions in range (no scope filter). */
  llmStatsViewAll: boolean;
  /** LLM Stats scope follows Fleet session when true. */
  llmStatsScopeLinked: boolean;
  /** LLM Stats scope path when unlinked (agent → kind → id). */
  llmStatsScopePath: [string, string, string, string];
  /** Local LLM tab: selected installed model (null = overview). */
  localLlmSelectedModel: string | null;
  /** Local LLM tab: auto-refresh while open. */
  localLlmAutoRefresh: boolean;
  /** Local LLM tab: append gateway logs filtered for ollama. */
  localLlmIncludeGatewayLogs: boolean;
  /** Fleet Control: keep Self improvement checked across sends and reloads. */
  chatSendSelfImprovement: boolean;
  /**
   * UX-PLAN Pillar F — per-session mirror for the three chat-send
   * toggles (`orchestrateWithAll`, `createNewProject`, `selfImprovement`).
   * Without this map, the toggles are three global booleans that leak
   * between sessions. With it, switching sessions restores each
   * toggle's per-session value. The global
   * `chatSendSelfImprovement` stays the legacy default for callers
   * that do not pass a session key.
   */
  chatSendOptionsBySession?: Record<
    string,
    {
      orchestrateWithAll: boolean;
      createNewProject: boolean;
      selfImprovement: boolean;
    }
  >;
  /** Fleet Control: default inbound queue mode when sending while a run is active. */
  fleetDefaultQueueMode: "steer" | "collect" | "followup" | "interrupt" | "steer-backlog";
  /**
   * UX-PLAN Pillar F — per-session queue mode override. The composer
   * uses `fleetDefaultQueueMode` as the global default; when a session
   * has an entry in this map, that mode wins. Mirrors the
   * `chatComposerModeBySession` pattern so toggles do not carry over
   * between sessions.
   */
  fleetDefaultQueueModeBySession?: Record<
    string,
    "steer" | "collect" | "followup" | "interrupt" | "steer-backlog"
  >;
  /** Fleet Control workspace layout preset. */
  fleetWorkspacePreset: FleetWorkspacePreset;
  /** Per-preset column/row weights and panel visibility. */
  fleetWorkspaceLayouts: Record<FleetWorkspacePreset, FleetWorkspaceLayoutState>;
  /** Fleet Control workspace density (panel chrome padding). */
  fleetWorkspaceDensity: import("./views/fleet-workspace-presets.ts").FleetWorkspaceDensity;
  /** Active inspector tab in Operator preset. */
  fleetInspectorTab: FleetInspectorTabId;
  /** Unified-layout: which right drawer tab is active (files / inspector). */
  fleetUnifiedDrawerTab: "files" | "inspector";
  /** Unified-layout: whether the right drawer is open. */
  fleetUnifiedDrawerOpen: boolean;
  /**
   * JAISIU-3320 / Pillar I — Durable session resume.
   * Session key the operator was last dispatched on a ≥3-child fan-out
   * for. Drives the "Return to active job" banner in the chat pane
   * when the active tab is something else. Mirrored to IndexedDB for
   * survival across reloads; this field is the localStorage mirror so
   * the renderer can read it synchronously.
   */
  pinnedActiveJob: string | null;
  /** Custom theme settings (accent color, presets, fonts). */
  customThemeSettings: CustomThemeSettings;
};

const FLEET_QUEUE_MODES = ["steer", "collect", "followup", "interrupt", "steer-backlog"] as const;

function normalizeFleetQueueMode(raw: unknown): UiSettings["fleetDefaultQueueMode"] {
  if (typeof raw === "string" && (FLEET_QUEUE_MODES as readonly string[]).includes(raw)) {
    return raw as UiSettings["fleetDefaultQueueMode"];
  }
  return "steer";
}

function migrateChatPanelsCollapsed(
  parsed: Partial<UiSettings> & {
    fleetSetupStripCollapsed?: boolean;
    chatWorkspacePanelOpen?: boolean;
  },
): FleetChatPanelsCollapsed {
  const legacy = parsed.chatPanelsCollapsed as
    | Partial<FleetChatPanelsCollapsed & { hint?: boolean }>
    | undefined;
  const setupFromLegacy =
    typeof legacy?.setup === "boolean"
      ? legacy.setup
      : typeof parsed.fleetSetupStripCollapsed === "boolean"
        ? parsed.fleetSetupStripCollapsed
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.setup;
  const workspaceFromLegacy =
    typeof legacy?.workspace === "boolean"
      ? legacy.workspace
      : typeof parsed.chatWorkspacePanelOpen === "boolean"
        ? !parsed.chatWorkspacePanelOpen
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.workspace;
  const orchestrationFromLegacy =
    typeof legacy?.orchestration === "boolean"
      ? legacy.orchestration
      : typeof legacy?.hint === "boolean"
        ? legacy.hint
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.orchestration;

  return {
    orchestration: orchestrationFromLegacy,
    activity:
      typeof legacy?.activity === "boolean"
        ? legacy.activity
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.activity,
    subagents:
      typeof legacy?.subagents === "boolean"
        ? legacy.subagents
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.subagents,
    sessionInspector:
      typeof legacy?.sessionInspector === "boolean"
        ? legacy.sessionInspector
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.sessionInspector,
    fleetSessions:
      typeof legacy?.fleetSessions === "boolean"
        ? legacy.fleetSessions
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.fleetSessions,
    environment:
      typeof legacy?.environment === "boolean"
        ? legacy.environment
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.environment,
    roster:
      typeof legacy?.roster === "boolean"
        ? legacy.roster
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.roster,
    fleetChecklist:
      typeof legacy?.fleetChecklist === "boolean"
        ? legacy.fleetChecklist
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.fleetChecklist,
    projects:
      typeof legacy?.projects === "boolean"
        ? legacy.projects
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.projects,
    workspace: workspaceFromLegacy,
    setup: setupFromLegacy,
    conversation:
      typeof legacy?.conversation === "boolean"
        ? legacy.conversation
        : DEFAULT_FLEET_CHAT_PANELS_COLLAPSED.conversation,
  };
}

export function loadSettings(): UiSettings {
  const defaultUrl = (() => {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}`;
  })();

  const defaults: UiSettings = {
    gatewayUrl: defaultUrl,
    token: "",
    sessionKey: "main",
    lastActiveSessionKey: "main",
    theme: "system",
    chatFocusMode: false,
    chatTranscriptMode: false,
    chatReasoningExpanded: false,
    chatAssistDisplay: DEFAULT_CHAT_ASSIST_DISPLAY,
    chatAutoOpenSubagentTabs: false,
    fleetAutoJumpToFailedChild: false,
    splitRatio: 0.6,
    navCollapsed: false,
    navGroupsCollapsed: {},
    chatOpenTabs: [],
    chatArchivedSessionKeys: [],
    favoriteChats: [],
    fleetChatHistoryCollapsed: false,
    inspectorWidth: 0,
    multiAgentHintDismissed: false,
    chatPanelsCollapsed: { ...DEFAULT_FLEET_CHAT_PANELS_COLLAPSED },
    fleetGraphNodePositions: {},
    fleetChatMainTab: DEFAULT_FLEET_CHAT_MAIN_TAB,
    fleetSessionChromeCollapsed: true,
    fleetSessionNavigatorScrollable: true,
    fleetChatComposerCollapsed: false,
    fleetComposerFileReviewCollapsed: false,
    chatComposerMode: "agent",
    chatComposerModeBySession: {},
    logsScopeLinked: true,
    logsScopePath: ["", "", "", ""],
    logsAreaFilters: ["agent", "skills"],
    logsIncludePlatform: false,
    logsViewAll: false,
    sessionsViewAll: true,
    sessionsScopeLinked: true,
    sessionsScopePath: ["", "", "", ""],
    llmStatsTab: "sessions",
    llmStatsViewAll: true,
    llmStatsScopeLinked: false,
    llmStatsScopePath: ["", "", "", ""],
    localLlmSelectedModel: null,
    localLlmAutoRefresh: true,
    localLlmIncludeGatewayLogs: true,
    chatSendSelfImprovement: false,
    chatSendOptionsBySession: {},
    fleetDefaultQueueMode: "steer",
    fleetDefaultQueueModeBySession: {},
    fleetWorkspacePreset: "unified",
    fleetWorkspaceLayouts: migrateFleetWorkspaceLayouts(undefined),
    fleetWorkspaceDensity: "comfortable",
    fleetInspectorTab: "agents",
    fleetUnifiedDrawerTab: "files",
    fleetUnifiedDrawerOpen: false,
    pinnedActiveJob: null,
    customThemeSettings: {
      preset: "default",
      customTheme: null,
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: 14,
    },
  };

  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return defaults;
    }
    const parsed = JSON.parse(raw) as Partial<UiSettings> & {
      fleetSetupStripCollapsed?: boolean;
      chatWorkspacePanelOpen?: boolean;
      chatPanelsCollapsed?: Partial<FleetChatPanelsCollapsed & { hint?: boolean }>;
      chatShowThinking?: boolean;
    };
    return {
      gatewayUrl:
        typeof parsed.gatewayUrl === "string" && parsed.gatewayUrl.trim()
          ? parsed.gatewayUrl.trim()
          : defaults.gatewayUrl,
      token: typeof parsed.token === "string" ? parsed.token : defaults.token,
      sessionKey: canonicalizeSessionKeyForRehydrate(
        typeof parsed.sessionKey === "string" && parsed.sessionKey.trim()
          ? parsed.sessionKey.trim()
          : defaults.sessionKey,
      ),
      lastActiveSessionKey: guardPersistedSessionKey(
        typeof parsed.lastActiveSessionKey === "string" && parsed.lastActiveSessionKey.trim()
          ? parsed.lastActiveSessionKey.trim()
          : (typeof parsed.sessionKey === "string" && parsed.sessionKey.trim()) ||
              defaults.lastActiveSessionKey,
      ),
      theme:
        parsed.theme === "light" || parsed.theme === "dark" || parsed.theme === "system"
          ? parsed.theme
          : defaults.theme,
      chatFocusMode:
        typeof parsed.chatFocusMode === "boolean" ? parsed.chatFocusMode : defaults.chatFocusMode,
      chatTranscriptMode:
        typeof parsed.chatTranscriptMode === "boolean"
          ? parsed.chatTranscriptMode
          : defaults.chatTranscriptMode,
      chatReasoningExpanded:
        typeof parsed.chatReasoningExpanded === "boolean"
          ? parsed.chatReasoningExpanded
          : defaults.chatReasoningExpanded,
      chatAssistDisplay: migrateChatAssistDisplay(parsed),
      chatAutoOpenSubagentTabs:
        // JAISIU-quantum-sync-storm — auto-open-on-spawn removed 2026-09.
        // Spawning a subagent used to flip the operator out of the parent
        // chat into the new subagent tab; we now render subagent rows in
        // the Fleet Map + activity panel instead, and never hijack the
        // chat session. The setting is kept on the type for forward-
        // compat (a future opt-in re-introduction can read it back), but
        // the effective value is pinned to `false` regardless of what the
        // operator had persisted previously.
        false,
      // JAISIU-fleet-autojump-default-off — auto-jump to failing child
      // (I5 violation by default) is now an opt-in setting. Existing
      // operators who had the previous unconditional behaviour get a
      // pinned `false` here, matching the auto-open-on-spawn precedent:
      // forward-compat field, but the effective behaviour is off.
      fleetAutoJumpToFailedChild: false,
      splitRatio:
        typeof parsed.splitRatio === "number" &&
        parsed.splitRatio >= 0.4 &&
        parsed.splitRatio <= 0.7
          ? parsed.splitRatio
          : defaults.splitRatio,
      navCollapsed:
        typeof parsed.navCollapsed === "boolean" ? parsed.navCollapsed : defaults.navCollapsed,
      navGroupsCollapsed:
        typeof parsed.navGroupsCollapsed === "object" && parsed.navGroupsCollapsed !== null
          ? parsed.navGroupsCollapsed
          : defaults.navGroupsCollapsed,
      chatOpenTabs: (() => {
        // JAISIU-XXXX — strip forks on the storage read path. A
        // persisted `chatOpenTabs` that contains `agent:*:fork:*`
        // entries would re-mount those tabs on the next page load,
        // pulling the operator's first message into a sidetracked
        // conversation. Replace each fork with the canonical "main"
        // alias so tab positions are preserved. Uses the shared
        // `isPersistedFork` predicate from `fork-session-key.ts` so
        // the regex cannot drift from the runtime rehydrate guards
        // in `app-settings.ts`.
        if (!Array.isArray(parsed.chatOpenTabs)) {
          return defaults.chatOpenTabs;
        }
        const out: string[] = [];
        for (const key of parsed.chatOpenTabs) {
          if (typeof key !== "string" || !key.trim()) {
            continue;
          }
          const trimmed = key.trim();
          out.push(isPersistedFork(trimmed) ? "main" : trimmed);
        }
        return out;
      })(),
      chatArchivedSessionKeys: Array.isArray(parsed.chatArchivedSessionKeys)
        ? parsed.chatArchivedSessionKeys.filter(
            (key): key is string => typeof key === "string" && Boolean(key.trim()),
          )
        : defaults.chatArchivedSessionKeys,
      favoriteChats: Array.isArray(parsed.favoriteChats)
        ? parsed.favoriteChats.filter(
            (key): key is string => typeof key === "string" && Boolean(key.trim()),
          )
        : defaults.favoriteChats,
      fleetChatHistoryCollapsed:
        typeof parsed.fleetChatHistoryCollapsed === "boolean"
          ? parsed.fleetChatHistoryCollapsed
          : defaults.fleetChatHistoryCollapsed,
      inspectorWidth:
        typeof parsed.inspectorWidth === "number" &&
        Number.isFinite(parsed.inspectorWidth) &&
        parsed.inspectorWidth >= 0
          ? parsed.inspectorWidth
          : defaults.inspectorWidth,
      multiAgentHintDismissed:
        typeof parsed.multiAgentHintDismissed === "boolean"
          ? parsed.multiAgentHintDismissed
          : defaults.multiAgentHintDismissed,
      chatPanelsCollapsed: migrateChatPanelsCollapsed(parsed),
      fleetGraphNodePositions:
        parsed.fleetGraphNodePositions &&
        typeof parsed.fleetGraphNodePositions === "object" &&
        !Array.isArray(parsed.fleetGraphNodePositions)
          ? (parsed.fleetGraphNodePositions as Record<string, { x: number; y: number }>)
          : defaults.fleetGraphNodePositions,
      fleetChatMainTab:
        parsed.fleetChatMainTab === "chat" ||
        parsed.fleetChatMainTab === "localLlm" ||
        parsed.fleetChatMainTab === "subagents" ||
        parsed.fleetChatMainTab === "projects" ||
        parsed.fleetChatMainTab === "files" ||
        parsed.fleetChatMainTab === "fleetMap"
          ? parsed.fleetChatMainTab
          : defaults.fleetChatMainTab,
      fleetSessionChromeCollapsed:
        typeof parsed.fleetSessionChromeCollapsed === "boolean"
          ? parsed.fleetSessionChromeCollapsed
          : defaults.fleetSessionChromeCollapsed,
      fleetSessionNavigatorScrollable:
        typeof parsed.fleetSessionNavigatorScrollable === "boolean"
          ? parsed.fleetSessionNavigatorScrollable
          : defaults.fleetSessionNavigatorScrollable,
      fleetChatComposerCollapsed:
        typeof parsed.fleetChatComposerCollapsed === "boolean"
          ? parsed.fleetChatComposerCollapsed
          : defaults.fleetChatComposerCollapsed,
      fleetComposerFileReviewCollapsed:
        typeof parsed.fleetComposerFileReviewCollapsed === "boolean"
          ? parsed.fleetComposerFileReviewCollapsed
          : defaults.fleetComposerFileReviewCollapsed,
      chatComposerMode:
        typeof parsed.chatComposerMode === "string" && isFleetComposerMode(parsed.chatComposerMode)
          ? parsed.chatComposerMode
          : defaults.chatComposerMode,
      chatComposerModeBySession: (() => {
        const raw = parsed.chatComposerModeBySession;
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
          return defaults.chatComposerModeBySession ?? {};
        }
        const out: Record<string, FleetComposerMode> = {};
        for (const [key, value] of Object.entries(raw)) {
          if (key.trim() && typeof value === "string" && isFleetComposerMode(value)) {
            out[key.trim()] = value;
          }
        }
        return out;
      })(),
      logsScopeLinked:
        typeof parsed.logsScopeLinked === "boolean"
          ? parsed.logsScopeLinked
          : defaults.logsScopeLinked,
      logsScopePath:
        Array.isArray(parsed.logsScopePath) && parsed.logsScopePath.length === 4
          ? (parsed.logsScopePath.map((s) => (typeof s === "string" ? s : "")) as [
              string,
              string,
              string,
              string,
            ])
          : defaults.logsScopePath,
      logsAreaFilters: Array.isArray(parsed.logsAreaFilters)
        ? parsed.logsAreaFilters.filter(
            (a): a is string => typeof a === "string" && Boolean(a.trim()),
          )
        : defaults.logsAreaFilters,
      logsIncludePlatform:
        typeof parsed.logsIncludePlatform === "boolean"
          ? parsed.logsIncludePlatform
          : defaults.logsIncludePlatform,
      logsViewAll:
        typeof parsed.logsViewAll === "boolean" ? parsed.logsViewAll : defaults.logsViewAll,
      sessionsViewAll:
        typeof parsed.sessionsViewAll === "boolean"
          ? parsed.sessionsViewAll
          : defaults.sessionsViewAll,
      sessionsScopeLinked:
        typeof parsed.sessionsScopeLinked === "boolean"
          ? parsed.sessionsScopeLinked
          : defaults.sessionsScopeLinked,
      sessionsScopePath:
        Array.isArray(parsed.sessionsScopePath) && parsed.sessionsScopePath.length === 4
          ? (parsed.sessionsScopePath.map((s) => (typeof s === "string" ? s : "")) as [
              string,
              string,
              string,
              string,
            ])
          : defaults.sessionsScopePath,
      llmStatsTab:
        parsed.llmStatsTab === "connect" ||
        parsed.llmStatsTab === "overview" ||
        parsed.llmStatsTab === "analytics" ||
        parsed.llmStatsTab === "fleet" ||
        parsed.llmStatsTab === "sessions" ||
        parsed.llmStatsTab === "trace" ||
        parsed.llmStatsTab === "local"
          ? parsed.llmStatsTab
          : defaults.llmStatsTab,
      llmStatsViewAll:
        typeof parsed.llmStatsViewAll === "boolean"
          ? parsed.llmStatsViewAll
          : defaults.llmStatsViewAll,
      llmStatsScopeLinked:
        typeof parsed.llmStatsScopeLinked === "boolean"
          ? parsed.llmStatsScopeLinked
          : defaults.llmStatsScopeLinked,
      llmStatsScopePath:
        Array.isArray(parsed.llmStatsScopePath) && parsed.llmStatsScopePath.length === 4
          ? (parsed.llmStatsScopePath.map((s) => (typeof s === "string" ? s : "")) as [
              string,
              string,
              string,
              string,
            ])
          : defaults.llmStatsScopePath,
      localLlmSelectedModel:
        typeof parsed.localLlmSelectedModel === "string" && parsed.localLlmSelectedModel.trim()
          ? parsed.localLlmSelectedModel.trim()
          : defaults.localLlmSelectedModel,
      localLlmAutoRefresh:
        typeof parsed.localLlmAutoRefresh === "boolean"
          ? parsed.localLlmAutoRefresh
          : defaults.localLlmAutoRefresh,
      localLlmIncludeGatewayLogs:
        typeof parsed.localLlmIncludeGatewayLogs === "boolean"
          ? parsed.localLlmIncludeGatewayLogs
          : defaults.localLlmIncludeGatewayLogs,
      chatSendSelfImprovement:
        typeof parsed.chatSendSelfImprovement === "boolean"
          ? parsed.chatSendSelfImprovement
          : defaults.chatSendSelfImprovement,
      // Pillar F — per-session mirror for orchestrate/create/self-improvement.
      // Invalid entries are silently dropped; missing fields fall back to
      // the defaults. The global `chatSendSelfImprovement` above remains
      // the legacy default for callers that do not pass a session key.
      chatSendOptionsBySession: normalizeChatSendOptionsBySession(parsed.chatSendOptionsBySession),
      fleetDefaultQueueMode: normalizeFleetQueueMode(
        parsed.fleetDefaultQueueMode ?? defaults.fleetDefaultQueueMode,
      ),
      // Pillar F — per-session queue mode mirror. Invalid entries
      // are silently dropped; missing keys fall back to the global
      // `fleetDefaultQueueMode`.
      fleetDefaultQueueModeBySession: (() => {
        const raw = parsed.fleetDefaultQueueModeBySession;
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
          return defaults.fleetDefaultQueueModeBySession ?? {};
        }
        const out: Record<
          string,
          "steer" | "collect" | "followup" | "interrupt" | "steer-backlog"
        > = {};
        for (const [key, value] of Object.entries(raw)) {
          if (
            key.trim() &&
            typeof value === "string" &&
            (FLEET_QUEUE_MODES as readonly string[]).includes(value)
          ) {
            out[key.trim()] = value as (typeof FLEET_QUEUE_MODES)[number];
          }
        }
        return out;
      })(),
      fleetWorkspacePreset: normalizeFleetWorkspacePreset(parsed.fleetWorkspacePreset),
      fleetWorkspaceLayouts: migrateFleetWorkspaceLayouts(parsed.fleetWorkspaceLayouts),
      fleetWorkspaceDensity: normalizeFleetWorkspaceDensity(parsed.fleetWorkspaceDensity),
      fleetInspectorTab: (() => {
        // JAISIU-types-sweep: `parsed` is typed with the CURRENT
        // `FleetInspectorTabId`, but this is a settings *migration* and
        // the bytes on disk can still hold a retired tab id. Read the
        // value at its persisted (string) width so the legacy branch
        // below is a real comparison rather than a no-op the compiler
        // (rightly) flagged as unreachable.
        const raw = parsed.fleetInspectorTab as FleetInspectorTabId | string | undefined;
        // JAISIU: Inspector "Projects" tab merged into "Files" — any
        // legacy "projects" selection lands on Files so the operator
        // never lands on a dead tab.
        if (raw === "projects") {
          return "files" as FleetInspectorTabId;
        }
        const valid = [
          "agents",
          "execution",
          "activity",
          "health",
          "files",
          "subagents",
          "document",
          "tunnels",
        ] as const;
        if (typeof raw === "string" && (valid as readonly string[]).includes(raw)) {
          return raw as FleetInspectorTabId;
        }
        if (
          parsed.fleetChatMainTab &&
          parsed.fleetChatMainTab !== "chat" &&
          parsed.fleetChatMainTab !== "fleetMap"
        ) {
          return fleetMainTabToInspectorTab(parsed.fleetChatMainTab);
        }
        return defaults.fleetInspectorTab;
      })(),
      fleetUnifiedDrawerTab: (() => {
        const raw = parsed.fleetUnifiedDrawerTab;
        // Legacy "monitor" drawer value has been folded into "inspector"
        // (the Monitor workspace preset is gone).
        if (raw === "files" || raw === "inspector") {
          return raw;
        }
        if (raw === "monitor") {
          return "inspector";
        }
        return defaults.fleetUnifiedDrawerTab;
      })(),
      fleetUnifiedDrawerOpen:
        typeof parsed.fleetUnifiedDrawerOpen === "boolean"
          ? parsed.fleetUnifiedDrawerOpen
          : defaults.fleetUnifiedDrawerOpen,
      pinnedActiveJob:
        typeof parsed.pinnedActiveJob === "string" && parsed.pinnedActiveJob.trim()
          ? parsed.pinnedActiveJob.trim()
          : defaults.pinnedActiveJob,
      customThemeSettings:
        parsed.customThemeSettings && typeof parsed.customThemeSettings === "object"
          ? {
              preset:
                parsed.customThemeSettings.preset === "custom" ||
                ["default", "ocean", "forest", "sunset", "midnight"].includes(
                  parsed.customThemeSettings.preset ?? "",
                )
                  ? (parsed.customThemeSettings.preset as import("./theme.ts").ThemePreset)
                  : defaults.customThemeSettings.preset,
              customTheme:
                parsed.customThemeSettings.customTheme &&
                typeof parsed.customThemeSettings.customTheme === "object"
                  ? (parsed.customThemeSettings.customTheme as import("./theme.ts").CustomTheme)
                  : defaults.customThemeSettings.customTheme,
              fontFamily:
                typeof parsed.customThemeSettings.fontFamily === "string"
                  ? parsed.customThemeSettings.fontFamily
                  : defaults.customThemeSettings.fontFamily,
              fontSize:
                typeof parsed.customThemeSettings.fontSize === "number" &&
                parsed.customThemeSettings.fontSize >= 10 &&
                parsed.customThemeSettings.fontSize <= 24
                  ? parsed.customThemeSettings.fontSize
                  : defaults.customThemeSettings.fontSize,
            }
          : defaults.customThemeSettings,
    };
  } catch {
    return defaults;
  }
}

export function saveSettings(next: UiSettings) {
  localStorage.setItem(KEY, JSON.stringify(next));
}

/**
 * Read the favorited chat-session keys from localStorage.
 *
 * Exposed so the chat-history sidebar does not have to thread the
 * favorites list through every parent prop (the sidebar is also
 * rendered standalone from `agents-import-panel`). Returns a defensive
 * copy — the caller may mutate the returned array freely. Errors
 * degrade to an empty list so a broken localStorage payload can never
 * break the sidebar's render.
 */
export function loadFavoriteChats(): string[] {
  try {
    const settings = loadSettings();
    return Array.isArray(settings.favoriteChats) ? settings.favoriteChats.slice() : [];
  } catch {
    return [];
  }
}

/**
 * Toggle a chat-session key in/out of the favorites list and persist
 * the new list to localStorage.
 *
 * Returns the new list (also persisted) — useful for callers that
 * want to update an in-memory cache without re-reading
 * `loadFavoriteChats()`.
 *
 * Safety:
 *  - blank / whitespace / non-string keys are rejected (no-op, returns
 *    the current list unchanged).
 *  - duplicates in the existing list are coalesced.
 *  - storage failures are swallowed and reported as an empty list so a
 *    broken `saveSettings()` (e.g. quota exceeded) cannot break the
 *    sidebar's toggle interaction.
 *
 * Exported for sidebar use and unit testing (JAISIU-3328 / UX-R3).
 */
export function toggleFavoriteChat(key: string): string[] {
  const trimmed = typeof key === "string" ? key.trim() : "";
  if (!trimmed) {
    return loadFavoriteChats();
  }
  try {
    const current = loadSettings();
    const list = Array.isArray(current.favoriteChats) ? current.favoriteChats : [];
    let next: string[];
    if (list.includes(trimmed)) {
      next = list.filter((k) => k !== trimmed);
    } else {
      next = [...list, trimmed];
    }
    saveSettings({ ...current, favoriteChats: next });
    return next;
  } catch {
    return [];
  }
}
